import {FC} from 'react'
import {Navigate, Route, Routes, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../template/layout/core'
import {AppsHeader} from './AppsHeader'
import {AppInfo} from './components/AppInfo'
import {AppLines} from './components/AppLines'
import {AmoCRMUsers} from './components/crm/amocrm/users/AmoCRMUsers'
import {BitrixUsers} from './components/crm/bitrix24/BitrixUsers'
import {appsAPI} from '../../services/AppsService'
import {Logs} from './components/logs/Logs'
import {AddLineBitrix24} from '../../template/partials/modals/add-line/ModalAddBitrix24Line'
import {BitrixAllUsers} from './components/crm/bitrix24/users/BitrixAllUsers'
import {AddLineAmoCRM} from '../../template/partials/modals/add-line/ModalAddAmocrmLine'
import {License} from './components/license/License'
import {AppAdmin} from './components/admin/AppAdmin'
import {useIntl} from 'react-intl'
import {usersAPI} from '../../services/UsersService'
import {EmptyAppPage} from './components/EmptyAppPage'
import {useRedirectOnAccountSwitch} from '../../hooks/useRedirectOnAccountSwitch'

const AppsPage: FC = () => {
  const intl = useIntl()
  const {appId} = useParams()
  useRedirectOnAccountSwitch()
  const {data: currentUser, isLoading: isCurrentUserLoading} = usersAPI.useFetchGetMeQuery()
  const {data: app} = appsAPI.useFetchAppByIdQuery(appId!)

  if (isCurrentUserLoading) {
    return null
  }

  if (app && !app.licenses) {
    return <EmptyAppPage error={{data: {detail: 'Not Found'}}} />
  }

  const accountBreadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.INTEGRATIONS'}),
      path: intl.formatMessage({id: 'BREADCRUMBS.INFO'}),
      isSeparator: false,
      isActive: false,
    },
  ]

  return (
    <Routes>
      <Route
        element={
          <>
            <PageTitle breadcrumbs={accountBreadCrumbs}>{intl.formatMessage({id: 'BREADCRUMBS.INFO'})}</PageTitle>
            <AppsHeader />
          </>
        }
      >
        <Route
          index
          element={
            <>
              <PageTitle breadcrumbs={accountBreadCrumbs}>{intl.formatMessage({id: 'BREADCRUMBS.INFO'})}</PageTitle>
              <AppInfo />
            </>
          }
        />
        <Route
          path='lines/'
          element={
            <>
              <PageTitle breadcrumbs={accountBreadCrumbs}>{intl.formatMessage({id: 'BREADCRUMBS.LINES'})}</PageTitle>
              <AppLines />
              {app?.crm === 'BITRIX24' ? <AddLineBitrix24 /> : <AddLineAmoCRM />}
            </>
          }
        />
        <Route
          path='users/'
          element={
            <>
              <PageTitle breadcrumbs={accountBreadCrumbs}>{intl.formatMessage({id: 'BREADCRUMBS.USERS'})}</PageTitle>
              {app?.crm === 'BITRIX24' && app.settings.bitrix24.users_in_service === true && <BitrixUsers />}
              {app?.crm === 'BITRIX24' && app.settings.bitrix24.users_in_service === false && <BitrixAllUsers />}
              {app?.crm === 'AMOCRM' && <AmoCRMUsers />}
            </>
          }
        />
        <Route
          path='license/'
          element={
            <>
              <PageTitle breadcrumbs={accountBreadCrumbs}>{intl.formatMessage({id: 'BREADCRUMBS.LICENSE'})}</PageTitle>
              <License />
            </>
          }
        />
        <Route
          path='logs/'
          element={
            <>
              <PageTitle breadcrumbs={accountBreadCrumbs}>{intl.formatMessage({id: 'BREADCRUMBS.LOGS'})}</PageTitle>
              <Logs />
            </>
          }
        />
        {currentUser?.role === 'ADMIN' && (
          <Route
            path='admin/'
            element={
              <>
                <PageTitle breadcrumbs={accountBreadCrumbs}>{intl.formatMessage({id: 'BREADCRUMBS.ADMIN'})}</PageTitle>
                <AppAdmin />
              </>
            }
          />
        )}
      </Route>
      <Route path='*' element={<Navigate to='/error/404' />} />
    </Routes>
  )
}

export default AppsPage
